/**
 * Whole-connection read for the board manager's `ListMyKanbanBoards` query
 * (SE-6575).
 *
 * `useBoardManager` previously issued a bare `{ first: 40 }` with no `after`
 * and no `fetchMore`, so an organization past 40 boards silently lost every
 * board beyond the first page — no empty state, no "load more", no indication
 * anything was missing. A board created elsewhere (or seen after any cache
 * drop) simply did not exist as far as the board manager was concerned.
 *
 * Raising the cap alone never closes this: `hasNextPage` was still true at
 * `first: 400` on staging. So this hook walks the connection instead, merging
 * every page onto the accumulated result via `updateQuery`.
 *
 * Four in-repo constraints shape the implementation:
 *
 *  1. `listMyKanbanBoards` is deliberately kept OUT of the Apollo cache field
 *     policies (`providers/ApolloClient.tsx` defines `relayStylePagination`
 *     for `listKanbanCards` but not for this field), because two queries
 *     (`ListMyKanbanBoards` and `PipelineSwitcherBoards`) select different
 *     field sets on the same connection and a shared field policy would
 *     cross-contaminate them. The merge therefore has to be explicit —
 *     `updateQuery`, mirroring `usePipelineSwitcherBoards`.
 *  2. The query runs `cache-and-network`, so a background page-1 fetch
 *     periodically REPLACES the merged connection with a single page while
 *     `hasNextPage` flips back to true. A one-shot walk guard would treat that
 *     as "already walked" and strand the list at one page, so the walk is
 *     restarted when the edge count drops — the refetch-detection pattern
 *     proven in `useKanbanBoardQueries`.
 *  3. That restart must not race the walk it replaces. Several call sites
 *     (`useBoardSharing`, `useKanbanBoardMutations`) issue
 *     `refetchQueries: ["ListMyKanbanBoards"]`, and deleting a board prunes an
 *     edge via `cache.modify` — each drops the edge count and could otherwise
 *     start a second walk while the first is still looping, merging pages out
 *     of server order. A generation counter invalidates the in-flight walk
 *     instead: a stale page is neither merged nor followed.
 *  4. `useBoardManager`'s create path injects the new board's edge with
 *     `cache.modify`. That edge can coexist with the same board arriving in a
 *     later page, so edges are de-duplicated by node id at the read boundary.
 *
 * Ordering is only sound because SE-6577 gave `listMyKanbanBoards` a stable
 * newest-first `ORDER BY`; before that a cursor pointed into an arbitrary
 * window.
 * @module features/player-kanban/hooks/useAllMyKanbanBoards
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  ListMyKanbanBoardsQuery,
  useListMyKanbanBoardsQuery,
} from "@/generated/graphql";

/**
 * Page size for the board-manager board list.
 *
 * Measured against staging on SE-6575: `first: 40` → 723ms / 19KB,
 * `first: 100` → 507ms / 47KB, `first: 200` → 609ms / 95KB. Latency is flat to
 * ~200, so 100 sits comfortably inside the cheap band while keeping each page's
 * payload modest — the query pulls `users(first: 100)` per board, which is what
 * actually drives payload growth.
 *
 * This is the page size for the `ListMyKanbanBoards` **board-manager** read
 * only. The other readers of this connection deliberately send different
 * `$first` values for different jobs (`useKanbanBoardQueries` asks for exactly
 * 1 as a cheap "any board?" probe; `AddToPipelineDialog` asks for its own
 * dialog page size), so they occupy separate normalized cache entries rather
 * than sharing this one.
 */
export const BOARD_LIST_PAGE_SIZE = 100;

/**
 * Hard cap on the number of ADDITIONAL pages walked after the first, so a
 * pathological board count cannot spin an unbounded fetch loop. At the page
 * size above this covers 100 * (1 + {@link MAX_BOARD_LIST_EXTRA_PAGES}) boards.
 * Hitting the cap is reported through `hasUnreachableBoards` so the UI can say
 * more boards exist instead of truncating silently.
 */
export const MAX_BOARD_LIST_EXTRA_PAGES = 19;

/** A single edge from the board-manager board query. */
export type BoardListEdge =
  ListMyKanbanBoardsQuery["listMyKanbanBoards"]["edges"][number];

/** Apollo's `fetchMore` for the `ListMyKanbanBoards` query. */
type BoardListFetchMore = ReturnType<
  typeof useListMyKanbanBoardsQuery
>["fetchMore"];

/**
 * How a connection walk ended.
 * - `complete` — the server reported a last page; the list is whole.
 * - `truncated` — the walk stopped early (page cap, a failed page, or a page
 *   that claimed more pages but gave no cursor to follow). Boards exist that
 *   are not in the list.
 * - `abandoned` — a newer walk superseded this one; it must not touch state.
 */
export type BoardWalkOutcome = "complete" | "truncated" | "abandoned";

/**
 * Merge one fetched board page onto the accumulated result by concatenating
 * edges and adopting the newest page's `pageInfo` (so the walk terminates).
 * @param previous - The result accumulated so far. May be absent when the cache
 * entry was evicted or the store reset mid-walk (e.g. logout), in which case
 * the fresh page stands alone.
 * @param next - The freshly fetched page (undefined on an empty refetch).
 * @returns The combined query result.
 */
export const mergeBoardListPages = (
  previous: ListMyKanbanBoardsQuery | undefined,
  next: ListMyKanbanBoardsQuery | undefined
): ListMyKanbanBoardsQuery | undefined => {
  if (!next) return previous;
  if (!previous?.listMyKanbanBoards) return next;
  return {
    ...next,
    listMyKanbanBoards: {
      ...next.listMyKanbanBoards,
      edges: [
        ...previous.listMyKanbanBoards.edges,
        ...next.listMyKanbanBoards.edges,
      ],
    },
  };
};

/**
 * Collapse edges to one per board `id`, keeping the first occurrence and
 * preserving order.
 *
 * Two paths can produce a duplicate: the create mutation's `cache.modify`
 * appends an edge for a board the server also returns in a later page, and an
 * `updateQuery` merge that races a background refetch can briefly hold the same
 * page twice. Either way the board strip must not render the board twice.
 * @param edges - The accumulated edges.
 * @returns Edges with at most one entry per board id.
 */
export const dedupeBoardEdgesById = (
  edges: readonly BoardListEdge[]
): readonly BoardListEdge[] =>
  // O(n²). An immutable O(n) form exists (build a Map keyed by node id and read
  // `.values()`), but it keeps the LAST edge per id where this keeps the first,
  // and first-wins is what protects the create path's `cache.modify` edge. The
  // bound is BOARD_LIST_PAGE_SIZE * (MAX_BOARD_LIST_EXTRA_PAGES + 1) edges, and
  // this matches the sibling `usePipelineSwitcherBoards`. Mutating a local
  // `Set` — the obvious O(n) fix — is barred by `functional/immutable-data`.
  edges.filter(
    (edge, index) =>
      edges.findIndex(other => other.node.id === edge.node.id) === index
  );

/** Everything a walk needs that does not change between its pages. */
interface BoardWalkContext {
  /** Apollo's `fetchMore` for the board query. */
  readonly fetchMore: BoardListFetchMore;
  /**
   * False once a newer walk has superseded this one. Checked after every await
   * so a stale page is neither merged into the cache nor followed.
   */
  readonly isCurrent: () => boolean;
}

/**
 * Walk the connection from `cursor` to its last page, merging each page in.
 * @param context - {@link BoardWalkContext} — `fetchMore` plus the staleness check.
 * @param cursor - The cursor to fetch after.
 * @param pagesWalked - How many additional pages this walk has already fetched.
 * @returns The {@link BoardWalkOutcome}.
 */
const walkFromCursor = async (
  context: BoardWalkContext,
  cursor: string,
  pagesWalked: number
): Promise<BoardWalkOutcome> => {
  if (pagesWalked >= MAX_BOARD_LIST_EXTRA_PAGES) {
    console.warn(
      "[useAllMyKanbanBoards] page cap reached; boards beyond " +
        `${BOARD_LIST_PAGE_SIZE * (MAX_BOARD_LIST_EXTRA_PAGES + 1)} ` +
        "are not shown"
    );
    return "truncated";
  }

  const result = await context
    .fetchMore({
      variables: { after: cursor },
      updateQuery: (previous, { fetchMoreResult }) =>
        // A superseded walk must not write its page into the cache: pages would
        // land out of server order and the newest-first ordering SE-6577
        // established would be lost.
        context.isCurrent()
          ? (mergeBoardListPages(previous, fetchMoreResult) ?? previous)
          : previous,
    })
    .catch((error: unknown) => {
      // A mid-walk rejection would otherwise truncate the list silently: the
      // connection still reports `hasNextPage`, but nothing is going to fetch
      // it. Surface it so the UI can say the list is incomplete.
      console.warn("[useAllMyKanbanBoards] fetchMore failed", error);
      return null;
    });

  if (!context.isCurrent()) return "abandoned";
  if (result === null) return "truncated";

  const nextPageInfo: ListMyKanbanBoardsQuery["listMyKanbanBoards"]["pageInfo"] =
    result.data.listMyKanbanBoards.pageInfo;

  if (!nextPageInfo.hasNextPage) return "complete";

  if (!nextPageInfo.endCursor) {
    // The server claims more pages but gave nothing to follow. Reporting
    // `complete` here would re-introduce exactly the silent truncation this
    // hook exists to remove, and the walk effect could never restart (it needs
    // an `endCursor`).
    console.warn(
      "[useAllMyKanbanBoards] hasNextPage with no endCursor; list is incomplete"
    );
    return "truncated";
  }

  return walkFromCursor(context, nextPageInfo.endCursor, pagesWalked + 1);
};

/** Return shape of {@link useAllMyKanbanBoards}. */
export interface UseAllMyKanbanBoardsResult {
  /** Every board edge across all fetched pages, de-duplicated by id. */
  readonly edges: readonly BoardListEdge[];
  /** Apollo loading state for the FIRST page only. */
  readonly loading: boolean;
  /**
   * True while additional pages are still being walked. Apollo's `loading` is
   * already false at this point (the first page has landed), so a consumer that
   * must not commit a decision on a partial board list — picking the active
   * board, say — has to gate on this as well.
   */
  readonly isWalkingPages: boolean;
  /** The first-page query error, if any. */
  readonly error: ReturnType<typeof useListMyKanbanBoardsQuery>["error"];
  /** Refetch the board set from the first page. */
  readonly refetch: ReturnType<typeof useListMyKanbanBoardsQuery>["refetch"];
  /**
   * True when the board list on hand is known NOT to be the whole set — either
   * the walk stopped while the connection still reported more pages (the page
   * cap was reached, a page request failed, or a page gave no cursor to
   * follow), or the FIRST page never landed at all because its request failed
   * at the transport (SE-7338). The UI must say the list is incomplete rather
   * than truncate silently — and, in the first-page case, rather than present
   * an empty strip as the authoritative answer.
   */
  readonly hasUnreachableBoards: boolean;
}

/**
 * Read the caller's WHOLE kanban board connection, walking every page so no
 * board is dropped past the first.
 * @returns The {@link UseAllMyKanbanBoardsResult}.
 */
export const useAllMyKanbanBoards = (): UseAllMyKanbanBoardsResult => {
  const { data, loading, error, fetchMore, refetch } =
    useListMyKanbanBoardsQuery({
      variables: { first: BOARD_LIST_PAGE_SIZE },
      fetchPolicy: "cache-and-network",
    });

  const [walkStoppedShort, setWalkStoppedShort] = useState(false);

  // Kept in a ref so the walk effect does not re-run (and re-walk) merely
  // because Apollo handed back a new `fetchMore` identity.
  const fetchMoreRef = useRef(fetchMore);

  // Bumped whenever the connection is replaced under us. Any walk whose
  // generation is no longer current abandons at its next checkpoint, so at most
  // one walk is ever live.
  const generationRef = useRef(0);
  const walkingGenerationRef = useRef<number | null>(null);
  const prevEdgeCountRef = useRef(0);

  const pageInfo = data?.listMyKanbanBoards?.pageInfo;
  const hasNextPage = pageInfo?.hasNextPage ?? false;
  const endCursor = pageInfo?.endCursor ?? null;
  const edgeCount = data?.listMyKanbanBoards?.edges?.length ?? 0;

  // DERIVED during render, never effect state. A consumer that must not commit
  // a decision on a partial list needs this to be true in the very commit the
  // first page lands — and that is the commit in which the walk-starting effect
  // has not run yet. A `useState` flag set from that effect reads `false` there
  // (same component, same flush, stale closure), so the guard it protects would
  // be inert exactly when it matters.
  //
  // The merged connection adopts each page's `pageInfo`, so `hasNextPage` stays
  // true until the last page merges — which makes this an accurate "more pages
  // are still coming" signal for the whole walk. It goes false when the walk
  // completes (`hasNextPage` false) or stops early (`hasUnreachableBoards`), so
  // it cannot stick true.
  // SE-7338: a first page that never landed because the client aborted its
  // transport (a co-batched POST hitting `BATCH_REQUEST_TIMEOUT_MS`) leaves an
  // EMPTY connection with `loading` already false. Nothing downstream can tell
  // that apart from "this user owns no boards", and the surface duly renders no
  // tabs, gates off the board-options menu, and auto-names the next created
  // board off `boards.length === 0`. The list is not short here, it is UNKNOWN
  // — report it as incomplete through the signal the surface already renders.
  // Scoped to a transport failure on an empty connection: a GraphQL-level error
  // that still returned edges is a different (and already-handled) situation.
  const firstPageUnreadable = error?.networkError != null && edgeCount === 0;

  const hasUnreachableBoards = walkStoppedShort || firstPageUnreadable;

  const isWalkingPages =
    hasNextPage && endCursor !== null && !hasUnreachableBoards;

  const edges = useMemo(
    (): readonly BoardListEdge[] =>
      dedupeBoardEdgesById(data?.listMyKanbanBoards?.edges ?? []),
    [data]
  );

  const startWalk = useCallback(
    async (cursor: string, generation: number): Promise<void> => {
      const isCurrent = () => generationRef.current === generation;
      const outcome = await walkFromCursor(
        { fetchMore: fetchMoreRef.current, isCurrent },
        cursor,
        0
      );

      if (outcome === "abandoned") return;

      // `walkingGenerationRef` deliberately KEEPS this generation. It records
      // "a walk has been run for this generation", not "a walk is running": the
      // merged connection's cursor advances as pages land, so releasing the ref
      // here would let the effect immediately start the walk over from the new
      // end cursor, forever. The only restart is a generation bump, which the
      // effect performs when the connection is replaced under us.
      setWalkStoppedShort(outcome === "truncated");
    },
    []
  );

  useEffect(() => {
    fetchMoreRef.current = fetchMore;
  }, [fetchMore]);

  useEffect(() => {
    // `cache-and-network` (and any `refetchQueries: ["ListMyKanbanBoards"]`)
    // periodically replaces the merged connection with a fresh page 1, and
    // deleting a board prunes an edge. Both show up as the edge count DROPPING
    // while the connection again reports more pages. Invalidate whatever walk
    // was running and let a fresh one start, rather than stranding the list at
    // one page or racing two walks against each other.
    if (edgeCount < prevEdgeCountRef.current && hasNextPage) {
      generationRef.current += 1;
      walkingGenerationRef.current = null;
    }
    prevEdgeCountRef.current = edgeCount;

    if (!hasNextPage || !endCursor) return;
    if (walkingGenerationRef.current === generationRef.current) return;

    walkingGenerationRef.current = generationRef.current;
    void startWalk(endCursor, generationRef.current);
  }, [edgeCount, hasNextPage, endCursor, startWalk]);

  return {
    edges,
    loading,
    isWalkingPages,
    error,
    refetch,
    hasUnreachableBoards,
  };
};
